import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setGiideTabState, updateStep } from '../../actions';
import { GetRecordingQuery } from '../../graphql/api';
import { IState, Recording, Step } from '../../graphql/types';
import { processRecoding } from '../../components/utils';
import { message } from 'antd';

const getRecordingQuery = gql`
  query GetRecording($id: ID!) {
    getRecording(id: $id) {
      id
      libraryRecordId
      tenantID
      giideId
      length
      filePath
      transcriptPath
      transcriptError
      deleted
      step {
        id
        title
        order
      }
      feedCards {
        items {
          id
          giideId
          recording {
            id
          }
          resourceType
          caption
          question
          questionType
          questionHasCorrectAnswer
          customHeader
          imageDetails {
            altDescription
            attributionFirstName
            attributionFullName
            attributionUserName
            attributionLastName
            caption
            description
            height
            imageTypeSelected
            url
            userUploadedImageDetails {
              customImageName
            }
            width
          }
          answerDetails {
            userAnswer
            correctAnswerIndex
            answerOptions {
              index
              sortOrder
              text
            }
          }
          firstName
          lastName
          expertise
          socialUrls {
            twitter
            linkedin
          }
          imageUrl
          iconUrl
          source
          time
          title
          type
          url
          context
          affiliation
          text
          bold
          term
          numbered
          listItems
          author
          unlinked
          customImage
          customImageName
          htmlText
          imageService
        }
      }
      transcriptResults {
        items {
          alternatives {
            content
          }
          type
          start_time
          end_time
        }
        transcript
      }
    }
  }
`;

const useRecording = (recordingId?: string) => {
  const dispatch = useDispatch();
  const step = useSelector<IState, Step | undefined>(state =>
    recordingId ? state.steps.find(step => step.id === recordingId) : undefined
  );
  const recording = step ? step?.recording : undefined;

  const fetchRecording = React.useCallback(
    async (id: string, force?: boolean, switchTab?: boolean) => {
      if (force || (recordingId && !recording?.transcriptResults?.items)) {
        const result = (await API.graphql(
          graphqlOperation(getRecordingQuery, { id })
        )) as {
          data: GetRecordingQuery;
        };
        const recording = result.data.getRecording as Recording;
        if (recording) {
          const recordingInput = (await processRecoding(recording).catch(
            err => {
              if (err !== 'transcriptError') {
                // we are handling transcriptError by dialog
                message.error('Failed to load transcripts results');
              }
            }
          )) as Recording;
          if (recordingInput) {
            dispatch(
              updateStep({ id: recording.id, recording: recordingInput })
            );
          } else {
            dispatch(updateStep({ id: recording.id, recording }));
          }

          if (recording.transcriptError) {
            console.error('Error during transcription process.');
            return;
          }

          if (
            switchTab &&
            (recording.transcriptResults || recordingInput.transcriptResults)
          ) {
            dispatch(setGiideTabState('transcript', recording.step.order));
          }
        }
      }
    },
    [recordingId, recording, dispatch]
  );

  React.useEffect(() => {
    if (!recording && recordingId && step?.ownRecordingUploaded) {
      fetchRecording(recordingId);
    }
  }, [fetchRecording, recording, recordingId, step?.ownRecordingUploaded]);

  return { recording, fetchRecording };
};

export default useRecording;
